import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { HelperText, IconButton, TextInput } from '@c2fo/liquidity';
import { PlusIcon, TrashCanIcon } from '@c2fo/liquidity/icons';
import useRecurringRuleFormContext from '../utils/useRecurringRuleFormContext';
import { CriteriaSelectProps } from './CriteriaSelect';

const InvoiceIds = ({
  categoryIndex,
  criteriaIndex,
  errors,
  setErrors,
}: Pick<CriteriaSelectProps, 'errors' | 'setErrors'> & {
  categoryIndex: number;
  criteriaIndex: number;
}) => {
  const { t } = useTranslation();
  const { control, getValues } = useRecurringRuleFormContext();
  const invoiceRules = getValues('invoiceRules');

  return (
    <>
      <span className="mb-4 text-sm font-normal text-text-secondary">
        {t('recurringRules.invoiceId.option.helperDescription')}
      </span>
      <Controller
        control={control}
        name={`invoiceRules.${categoryIndex}.criteria.${criteriaIndex}.value`}
        render={({ field }) => {
          const valueArray = Array.isArray(field.value) ? field.value : [''];

          return (
            <div className="space-y-4">
              {valueArray.map((item, index) => (
                <div key={index} className="flex w-full flex-row items-center justify-between ">
                  <div className="flex w-full flex-row items-center gap-2">
                    <div className="flex w-full lg:w-[396px]">
                      <div className="flex w-full flex-col">
                        <TextInput
                          aria-label={invoiceRules[categoryIndex].criteria[criteriaIndex].type}
                          className="w-full"
                          placeholder={t('recurringRules.invoiceId.placeholder')}
                          onChange={(e) => {
                            const newValue = [...valueArray];
                            newValue[index] = e.target.value;
                            field.onChange(newValue);

                            if (e.target.value.length > 15) {
                              setErrors([
                                ...errors,
                                {
                                  type: 'excludedVoucherIds',
                                  message: t('invoiceSettings.rules.invoiceIdErrorInvalid'),
                                },
                              ]);
                            } else {
                              setErrors(errors.filter((error) => error.type !== 'excludedVoucherIds'));
                            }
                          }}
                          value={item ?? ''}
                        />
                        <div className="mt-1 text-right">
                          <HelperText>{item.length ? item.length : 0}/15</HelperText>
                        </div>
                      </div>
                    </div>
                    {index === valueArray.length - 1 && valueArray.length < 5 && (
                      <IconButton
                        className="mb-6"
                        icon={PlusIcon}
                        name={t('recurringRules.invoiceId.addAnother')}
                        onClick={() => {
                          const currentError = errors.find((error) => error.type === 'excludedVoucherIds');

                          if (valueArray[valueArray.length - 1].length === 0) {
                            setErrors([
                              ...errors,
                              {
                                type: 'excludedVoucherIds',
                                message: t('invoiceSettings.rules.invoiceIdErrorEmpty'),
                              },
                            ]);
                          } else if (currentError) {
                            // If there is any existing error, prevent adding a new field
                            setErrors([...errors]);
                          } else {
                            // Clear the error related to excludedVoucherIds if the current field is valid
                            setErrors(errors.filter((error) => error.type !== 'excludedVoucherIds'));

                            field.onChange([...valueArray, '']);
                          }
                        }}
                        size="sm"
                        variant="ancillary"
                      />
                    )}
                  </div>
                  {valueArray.length > 1 && (
                    <div className="grow text-right">
                      <IconButton
                        className="mb-6 bg-transparent"
                        icon={TrashCanIcon}
                        inverse
                        name={t('recurringRules.invoiceId.remove')}
                        onClick={() => {
                          // Clear the error related to excludedVoucherIds if the current field is valid
                          setErrors(errors.filter((error) => error.type !== 'excludedVoucherIds'));

                          const newValue = [...valueArray];
                          newValue.splice(index, 1);
                          field.onChange(newValue.length ? newValue : ['']);
                        }}
                        size="sm"
                        variant="destructive"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        }}
      />
    </>
  );
};

export default InvoiceIds;
